/**
 * LUDOPIA — identité, bourse et apparences partagées entre les jeux de la plateforme.
 *
 * ══ Ce que Ludopia est, et ce qu'il ne remplace pas ══════════════════════════
 *
 * Ludopia centralise l'identité (un pseudo, un avatar), une monnaie commune (les Ludos)
 * et des cosmétiques traversant les jeux. Villopia s'y **branche**, il ne s'y dissout pas :
 *
 * · **le compte Supabase reste l'identité de la SAUVEGARDE.** C'est lui qui possède la
 *   ville, le slug public et la ligne en base. Le remplacer demanderait une migration de
 *   toutes les villes existantes, et surtout rendrait le jeu injouable sans compte — ce
 *   que le §1 interdit explicitement (« jouable en < 5 s sans compte ») ;
 * · **les gemmes restent les gemmes.** Elles sont verrouillées par des privilèges de
 *   colonne (étape 2), adossées à Stripe, et le §14 encadre ce qu'elles achètent. Les
 *   Ludos sont une monnaie de PLATEFORME, plafonnée à 1 000 par jour et par jeu :
 *   confondre les deux ferait entrer un plafond externe dans l'économie du §6.
 *
 * Ludopia est donc une couche EN PLUS, jamais une couche EN DESSOUS. Un joueur qui ne s'y
 * connecte jamais joue exactement le même jeu.
 *
 * ══ Les trois principes du guide, et où ils sont tenus ═══════════════════════
 *
 * 1. **« Ne jamais afficher un champ mot de passe Ludopia. »** Ce module n'expose aucun
 *    formulaire : la connexion part vers `ludopia.fr` et revient. Le mot de passe du
 *    joueur ne traverse jamais Villopia — c'est tout l'objet d'OAuth, et le rappeler ici
 *    évite qu'un écran « pratique » le contourne un jour ;
 * 2. **un seul solde fait foi** — celui de Ludopia. Ce module ne met rien en cache au-delà
 *    de l'affichage courant, et toute opération relit le solde rendu par le serveur ;
 * 3. **chaque mouvement porte une référence unique et RECALCULABLE.** Pas un aléa : une
 *    référence dérivée de ce qui l'a produit, pour qu'un rejeu rende `deja: true` au lieu
 *    de créditer deux fois.
 *
 * ══ Les pièges documentés, traités nommément ════════════════════════════════
 *
 * · **le code d'autorisation vaut cinq minutes et un seul usage.** On l'échange
 *   immédiatement au retour, et on l'efface de l'URL dans la foulée — un rechargement de
 *   la page de retour rejouerait sinon un code mort et afficherait une erreur au joueur ;
 * · **l'adresse de retour est comparée à l'identique.** Elle est donc dérivée de
 *   `location.origin`, jamais écrite en dur : une barre oblique de trop et le retour
 *   échoue avec un message qui ne dit pas laquelle ;
 * · **un jeton mort ne se rafraîchit pas en silence.** `401` remet l'état à « déconnecté »
 *   et le dit à l'appelant, plutôt que de réessayer en boucle.
 */

/** Service de la plateforme. Une seule adresse, pour qu'elle ne diverge nulle part. */
const SERVICE = 'https://ludopia-social.bgibiard.workers.dev';

/** Identifiant du jeu, tel que déclaré à l'enrôlement. */
const JEU = 'villopia';

/** Où l'on range le jeton du joueur. Préfixé, comme tout le stockage du jeu. */
const JETON_KEY = 'villopia:ludopia:jeton';

/**
 * Où l'on range le vérificateur PKCE, le temps de l'aller-retour.
 *
 * ⚠️ `sessionStorage` et non `localStorage` : le vérificateur ne doit pas survivre à la
 * fermeture de l'onglet. C'est un secret à usage unique dont la durée de vie utile est
 * celle d'une redirection.
 */
const VERIF_KEY = 'villopia:ludopia:verificateur';

export interface LudopiaJoueur {
  readonly id: string;
  readonly pseudo: string;
  readonly avatar?: string;
  readonly accent?: string;
}

export interface LudopiaBourse {
  readonly solde: number;
  readonly gagne: number;
  readonly depense: number;
  readonly plafondJour: number;
  readonly gainsDuJour: number;
}

/** Le jeton courant, ou `null`. */
export function ludopiaJeton(): string | null {
  try {
    const value = localStorage.getItem(JETON_KEY);
    return value === null || value === '' ? null : value;
  } catch {
    return null;
  }
}

/**
 * L'adresse de retour, DÉRIVÉE de l'origine courante.
 *
 * ⚠️ Ludopia la compare à l'identique à celles déclarées à l'enrôlement. L'écrire en dur
 * obligerait à maintenir la même chaîne à trois endroits — le manifeste d'enrôlement, ce
 * fichier, et la page de retour — et deux d'entre eux finiraient par diverger.
 */
export function ludopiaRetour(): string {
  return `${location.origin}/connexion`;
}

/** Encode en base64url, la seule forme acceptée par PKCE. */
function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Démarre la connexion : fabrique le couple PKCE et part vers Ludopia.
 *
 * Le vérificateur reste ICI, le défi part là-bas. C'est toute la propriété de PKCE : même
 * quelqu'un qui intercepte le code d'autorisation ne peut pas l'échanger sans le
 * vérificateur, qui n'a jamais quitté ce navigateur.
 */
export async function ludopiaConnexion(): Promise<void> {
  const octets = new Uint8Array(32);
  globalThis.crypto.getRandomValues(octets);
  const verificateur = base64url(octets);

  const digest = await globalThis.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verificateur),
  );
  const defi = base64url(new Uint8Array(digest));

  try {
    sessionStorage.setItem(VERIF_KEY, verificateur);
  } catch {
    // Stockage refusé (navigation privée stricte) : inutile de partir, le retour
    // échouerait sans vérificateur. Mieux vaut ne rien faire que promener le joueur.
    return;
  }

  const url = new URL('https://ludopia.fr/connexion');
  url.searchParams.set('jeu', JEU);
  url.searchParams.set('retour', ludopiaRetour());
  url.searchParams.set('defi', defi);
  location.assign(url.toString());
}

export type LudopiaEchange =
  | { readonly ok: true; readonly joueur: LudopiaJoueur }
  | { readonly ok: false; readonly raison: 'sans-verificateur' | 'refuse' | 'reseau' };

/**
 * Échange le code d'autorisation contre un jeton. À appeler sur la page de retour.
 *
 * ⚠️ Le vérificateur est CONSOMMÉ avant tout appel réseau, quelle qu'en soit l'issue. Le
 * code ne vaut qu'un seul usage : le garder en réserve n'aiderait pas à réessayer, et le
 * laisser traîner en ferait un secret dormant.
 */
export async function ludopiaEchanger(code: string): Promise<LudopiaEchange> {
  let verificateur: string | null;
  try {
    verificateur = sessionStorage.getItem(VERIF_KEY);
    sessionStorage.removeItem(VERIF_KEY);
  } catch {
    verificateur = null;
  }
  if (verificateur === null || verificateur === '')
    return { ok: false, raison: 'sans-verificateur' };

  try {
    const response = await fetch(`${SERVICE}/oauth/jeton`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code, verificateur, jeu: JEU }),
    });
    if (!response.ok) return { ok: false, raison: 'refuse' };

    const corps = (await response.json()) as { jeton?: string; joueur?: LudopiaJoueur };
    if (typeof corps.jeton !== 'string' || corps.joueur === undefined) {
      return { ok: false, raison: 'refuse' };
    }

    localStorage.setItem(JETON_KEY, corps.jeton);
    return { ok: true, joueur: corps.joueur };
  } catch {
    return { ok: false, raison: 'reseau' };
  }
}

/** Appel authentifié. Rend `null` sur 401 APRÈS avoir oublié le jeton (piège n°3). */
async function authentifie<T>(chemin: string, init?: RequestInit): Promise<T | null> {
  const jeton = ludopiaJeton();
  if (jeton === null) return null;

  try {
    const response = await fetch(`${SERVICE}${chemin}`, {
      ...init,
      /*
        ⚠️ `new Headers(...)` et non un étalement d'objet. `HeadersInit` accepte aussi un
        TABLEAU de paires : l'étaler dans un littéral produirait `{ '0': [...], '1': [...] }`
        — des en-têtes nommés « 0 » et « 1 », donc aucun en-tête utile, et une requête
        refusée sans que rien ne dise pourquoi.
      */
      headers: (() => {
        const headers = new Headers(init?.headers);
        headers.set('authorization', `Bearer ${jeton}`);
        headers.set('content-type', 'application/json');
        return headers;
      })(),
    });
    /*
      ⚠️ 401 = session expirée. On OUBLIE le jeton au lieu de retenter : un jeton mort qui
      reste en place fait échouer tous les appels suivants, en silence, jusqu'à ce que le
      joueur vide son stockage. Le guide nomme ce piège ; le remède est d'accepter la
      déconnexion et de la rendre visible.
    */
    if (response.status === 401) {
      ludopiaDeconnexionLocale();
      return null;
    }
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

/** Le joueur connecté, ou `null`. */
export function ludopiaMoi(): Promise<LudopiaJoueur | null> {
  return authentifie<LudopiaJoueur>('/jeu/moi');
}

/** La bourse du joueur, ou `null`. */
export function ludopiaBourse(): Promise<LudopiaBourse | null> {
  return authentifie<LudopiaBourse>('/jeu/bourse');
}

/** Oublie le jeton localement, sans prévenir le serveur. */
export function ludopiaDeconnexionLocale(): void {
  try {
    localStorage.removeItem(JETON_KEY);
  } catch {
    // Rien à faire : sans stockage, il n'y avait pas de jeton à oublier.
  }
}

/** Déconnecte proprement : le serveur d'abord, le stockage ensuite. */
export async function ludopiaDeconnexion(): Promise<void> {
  await authentifie('/jeu/deconnexion', { method: 'POST' });
  ludopiaDeconnexionLocale();
}
